import {
  ArrowUpRightIcon,
  BuildingOffice2Icon,
  ChatBubbleLeftRightIcon,
  CircleStackIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  Cog8ToothIcon,
  DocumentArrowUpIcon,
  Link,
  LinkIcon,
  PageHero,
  PuzzlePieceIcon,
  TagIcon,
  useUserPermissions,
  UserGroupIcon,
} from "@";

/**
 * Settings hub — organized into 3 logical sections:
 *  • المتجر       — workspace fundamentals (general, org info, staff)
 *  • التكاملات   — external services (payments, WhatsApp, SMS, pixels, message templates)
 *  • البيانات والسجل — data + records (classifications, import, logs)
 *
 * A tile is shown if the user has ANY of its comma-separated permission keys.
 * A section is shown if it has at least one visible tile.
 */

const SECTIONS = [
  {
    key: "workspace",
    eyebrow: "المتجر",
    title: "الإعدادات الأساسية",
    icon: BuildingOffice2Icon,
    tiles: [
      {
        title: "الإعدادات العامة",
        description: "التحكم في الإعدادات الأساسية للمنصة.",
        href: "/settings-general",
        icon: Cog8ToothIcon,
        permission: "generalSettings",
      },
      {
        title: "عن الجمعية",
        description: "بيانات الجمعية، التراخيص، الحسابات البنكية، والتواصل.",
        href: "/association-manager",
        icon: BuildingOffice2Icon,
        permission: "associationData,editAssociationData",
      },
      {
        title: "موظفي المتجر",
        description: "إدارة المستخدمين والصلاحيات والأدوار.",
        href: "/settings-users",
        icon: UserGroupIcon,
        permission: "permissions",
      },
    ],
  },
  {
    key: "integrations",
    eyebrow: "التكاملات",
    title: "الخدمات الخارجية",
    icon: PuzzlePieceIcon,
    tiles: [
      // {
      //   title: "التكاملات الخارجية",
      //   description: "بوابات الدفع، مزوّدو WhatsApp، والرسائل القصيرة.",
      //   href: "/settings-integrations",
      //   icon: PuzzlePieceIcon,
      //   permission: "serviceLinkSettings",
      //   badge: "جديد",
      // },
      {
        title: "ربط الخدمات",
        description: "Google Tag Manager، Hotjar، وسكربتات التتبع.",
        href: "/settings-services-connection",
        icon: LinkIcon,
        permission: "serviceLinkSettings",
      },
      {
        title: "التحكم في الرسائل",
        description: "نصوص الرسائل المؤتمتة وقوالب التنبيهات.",
        href: "/settings-control",
        icon: ChatBubbleLeftRightIcon,
        permission: "editSmsMessage",
      },
    ],
  },
  {
    key: "data",
    eyebrow: "البيانات والسجل",
    title: "البيانات والمحتوى",
    icon: CircleStackIcon,
    tiles: [
      {
        title: "التصنيفات",
        description: "تنظيم وتصنيف عناصر المنصة.",
        href: "/classifications",
        icon: TagIcon,
        permission: "editAssociationData",
      },
      {
        title: "استيراد البيانات",
        description: "رفع ملفات Excel لإضافة بيانات بشكل جماعي.",
        href: "/import-data",
        icon: DocumentArrowUpIcon,
        permission: "importData",
      },
      {
        title: "سجل الأحداث",
        description: "تتبع آخر العمليات والتعديلات على النظام.",
        href: "/logs",
        icon: ClipboardDocumentListIcon,
        permission: "showLogs",
      },
    ],
  },
];

// Any-of permission check against the comma-separated keys.
const hasAccess = (permissionMap, perm) =>
  perm
    .split(",")
    .map((p) => p.trim())
    .some((p) => permissionMap?.[p]);

const NavTile = ({ tile }) => {
  const Icon = tile.icon;
  return (
    <Link
      to={tile.href}
      className="group relative flex h-full flex-col gap-3 overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-purple-200 hover:shadow-md hover:shadow-purple-900/5 dark:border-gray-600 dark:bg-gray-700 dark:hover:border-purple-700/40"
    >
      <div className="flex items-start justify-between gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-purple-50 to-purple-100/60 text-purple-900 ring-1 ring-purple-100 transition-transform duration-200 group-hover:scale-105 dark:from-purple-950/60 dark:to-purple-900/40 dark:text-purple-300 dark:ring-purple-800/40">
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </span>

        {tile.badge && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-800/50">
            {tile.badge}
          </span>
        )}
      </div>

      <div className="flex-1">
        <h3 className="text-[15px] font-bold tracking-tight text-gray-900 dark:text-gray-100">
          {tile.title}
        </h3>
        <p className="mt-1 text-[13px] leading-relaxed text-gray-500 dark:text-gray-400">
          {tile.description}
        </p>
      </div>

      <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-gray-400 transition-colors group-hover:text-purple-700 dark:text-gray-500 dark:group-hover:text-purple-300">
        فتح
        <ArrowUpRightIcon className="h-3 w-3" strokeWidth={2.5} />
      </span>
    </Link>
  );
};

const Section = ({ section, tiles }) => {
  const Icon = section.icon;
  return (
    <section aria-labelledby={`settings-section-${section.key}`}>
      <div className="mb-3 flex items-center gap-3 px-1">
        <span
          aria-hidden
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-purple-800 to-purple-950 text-white shadow-sm shadow-purple-900/15 ring-1 ring-purple-900/10 dark:from-purple-600 dark:to-purple-800"
        >
          <Icon className="h-4 w-4" strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-purple-700 dark:text-purple-400">
            {section.eyebrow}
          </p>
          <h2
            id={`settings-section-${section.key}`}
            className="text-base font-bold tracking-tight text-gray-900 dark:text-gray-100"
          >
            {section.title}
          </h2>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {tiles.map((tile) => (
          <NavTile key={tile.href} tile={tile} />
        ))}
      </div>
    </section>
  );
};

const Index = () => {
  const { permissions } = useUserPermissions();

  const visibleSections = SECTIONS.map((section) => ({
    section,
    tiles: section.tiles.filter((tile) => hasAccess(permissions, tile.permission)),
  })).filter((entry) => entry.tiles.length > 0);

  const totalTiles = visibleSections.reduce(
    (sum, entry) => sum + entry.tiles.length,
    0,
  );

  return (
    <main className="md:pt-8 pt-5">
      <div className="table-card relative pb-5">
      <PageHero
        eyebrow="الإعدادات"
        title="مركز الإعدادات"
        icon={Cog6ToothIcon}
        count={totalTiles}
      />

      <p className="mb-6 max-w-2xl px-1 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
        إدارة كل ما يخص المتجر من مكان واحد — اختر القسم الذي تريد التعديل
        فيه.
      </p>

      <div className="space-y-8">
        {visibleSections.map(({ section, tiles }) => (
          <Section key={section.key} section={section} tiles={tiles} />
        ))}
      </div>
      </div>
    </main>
  );
};

export default Index;
